import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import {
  Plus,
  CheckCircle,
  XCircle,
  Calendar,
  Clock,
  Sparkles,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Spinner } from "../components/ui/shadcn-io/spinner";
import { DataTable } from "../components/dataTable";
import {
  CompOff,
  createEmployeeCompOffColumns,
} from "../lib/compOffTableColumns";

export default function EmployeeCompOff() {
  const navigate = useNavigate();
  const location = useLocation();
  const [compOffs, setCompOffs] = useState<CompOff[]>([]);
  const [availableCompOffs, setAvailableCompOffs] = useState<CompOff[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [selectedCompOff, setSelectedCompOff] = useState<CompOff | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      window.history.replaceState({}, document.title);

      setTimeout(() => {
        setSuccessMessage("");
      }, 5000);
    }
  }, [location.state]);

  useEffect(() => {
    fetchCompOffs();
    fetchAvailable();
  }, [statusFilter]);

  const fetchCompOffs = async () => {
    try {
      setIsLoading(true);
      setError("");
      const token = localStorage.getItem("authToken");
      const statusQuery =
        statusFilter === "all" ? "" : `?status=${statusFilter}`;
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/compoff/my${statusQuery}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const normalized = (response.data || []).map((item: any) => ({
        ...item,
        id: item._id,
        status: item.status || (item.isExhausted ? "Exhausted" : "Pending"),
        requestedDate:
          item.requestedDate || item.appliedDate || item.createdAt || item.updatedAt,
      }));
      setCompOffs(normalized);
    } catch (err: any) {
      console.error("Error fetching comp-offs:", err);
      setError(
        err.response?.data?.message || "Failed to fetch comp-off requests"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAvailable = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/compoff/available`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const normalized = (response.data || []).map((item: any) => ({
        ...item,
        id: item._id,
        status: item.status || "Approved",
      }));
      setAvailableCompOffs(normalized);
    } catch (err) {
      console.error("Error fetching available comp-offs:", err);
    }
  };

  const handleViewClick = (compOff: CompOff) => {
    setSelectedCompOff(compOff);
    setIsViewDialogOpen(true);
  };

  const columns = useMemo(
    () => createEmployeeCompOffColumns(handleViewClick),
    []
  );

  const stats = useMemo(() => {
    const pending = compOffs.filter((c) => c.status === "Pending").length;
    const approved = compOffs.filter((c) => c.status === "Approved");
    const approvedCredits = approved.reduce(
      (sum, c) => sum + (c.creditDays || 0),
      0
    );
    return {
      pending,
      approvedCount: approved.length,
      approvedCredits,
      availableCount: availableCompOffs.length,
    };
  }, [compOffs, availableCompOffs]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-2">
          <Spinner variant="ellipsis" size={40} />
          <p className="text-sm text-muted-foreground">
            Loading your comp-off requests...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">My Comp-Offs</h1>
          <p className="text-muted-foreground mt-1">
            Track requests and available comp-off credits
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate("/employee/leaves/apply")}
          >
            <Calendar className="mr-2 h-4 w-4" />
            Use Comp-Off in Leave
          </Button>
          <Button onClick={() => navigate("/employee/compoff/apply")}>
            <Plus className="mr-2 h-4 w-4" />
            Apply for Comp-Off
          </Button>
        </div>
      </div>

      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-green-800 font-medium">{successMessage}</p>
          </div>
          <button
            onClick={() => setSuccessMessage("")}
            className="text-green-600 hover:text-green-800"
          >
            ×
          </button>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-800 font-medium">{error}</p>
          </div>
          <button
            onClick={() => setError("")}
            className="text-red-600 hover:text-red-800"
          >
            ×
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Available Credits
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              <span className="text-2xl font-bold">{stats.availableCount}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Ready to use while applying leave
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Approved Credits
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="text-2xl font-bold">
                {stats.approvedCredits} day(s)
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Across {stats.approvedCount} approved request(s)
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pending Requests
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-600" />
              <span className="text-2xl font-bold">{stats.pending}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Bucket Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            {availableCompOffs.length ? (
              <div className="space-y-2 text-sm">
                {availableCompOffs.slice(0, 2).map((c) => (
                  <div
                    key={c._id}
                    className="flex items-center justify-between bg-muted rounded-md px-3 py-2"
                  >
                    <div>
                      <p className="font-medium">
                        {c.creditDays} day(s) credit
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Earned on {new Date(c.workDate).toLocaleDateString()}
                      </p>
                    </div>
                    {c.expiryDate && (
                      <Badge variant="outline">
                        Expires {new Date(c.expiryDate).toLocaleDateString()}
                      </Badge>
                    )}
                  </div>
                ))}
                {availableCompOffs.length > 2 && (
                  <p className="text-xs text-muted-foreground">
                    + {availableCompOffs.length - 2} more available
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No approved comp-off credits yet.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="mb-6">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Requests</SelectItem>
            <SelectItem value="Pending">Pending</SelectItem>
            <SelectItem value="Approved">Approved</SelectItem>
            <SelectItem value="Rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable columns={columns} data={compOffs} />

      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Comp-Off Request Details</DialogTitle>
            <DialogDescription>
              Complete information about your comp-off request
            </DialogDescription>
          </DialogHeader>

          {selectedCompOff && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Worked On</Label>
                  <p className="font-medium mt-1">
                    {new Date(selectedCompOff.workDate).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Credit Days</Label>
                  <p className="font-medium mt-1">
                    {selectedCompOff.creditDays}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Hours Worked</Label>
                  <p className="font-medium mt-1">
                    {selectedCompOff.hoursWorked} hrs
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <div className="mt-1">
                    {selectedCompOff.status && (
                      <Badge
                        className={
                          selectedCompOff.status === "Approved"
                            ? "bg-green-100 text-green-800"
                            : selectedCompOff.status === "Rejected"
                            ? "bg-red-100 text-red-800"
                            : "bg-yellow-100 text-yellow-800"
                        }
                      >
                        {selectedCompOff.status}
                      </Badge>
                    )}
                  </div>
                </div>
                {selectedCompOff.expiryDate && (
                  <div>
                    <Label className="text-muted-foreground">Expiry</Label>
                    <p className="font-medium mt-1">
                      {new Date(
                        selectedCompOff.expiryDate
                      ).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-muted-foreground">Reason</Label>
                <p className="mt-1 p-3 bg-muted rounded-md">
                  {selectedCompOff.reason}
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsViewDialogOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
